import React, { useState } from 'react';
import * as Sentry from "@sentry/react";
import i18n from '../translation';
import useAuth from "system/APIAuth";
import { Box, TextField, Alert, AlertColor, Grow, InputAdornment, Typography, Button, Stack } from '@mui/material';
import { AccountCircle, Key, Lock } from '@mui/icons-material';
import APIRequest from 'system/APIRequest';
import { useSnackbar } from 'notistack';
import { TextLoader } from './apiHandling/TextLoader';
import { GoogleLogin } from '@react-oauth/google';
import MicrosoftLogin from 'react-microsoft-login';

type LoginFormProps = {
  onLogin?: () => void;
  message?: string;
}

// This is a form for the user to provide login information
// It fills the width of its container, providing email and password ValidatedInput fields, and a submit button
export default function LoginForm(props: LoginFormProps) {
  const { enqueueSnackbar } = useSnackbar();

  // Set initial (default) state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginInflight, setLoginInflight] = useState(false);
  const [emailError, setEmailError] = useState<string | undefined>(undefined);
  const [passwordError, setPasswordError] = useState<string | undefined>(undefined);
  const [passwordResetPrompt, setPasswordResetPrompt] = useState<boolean>(false);
  const [loginError, setLoginError] = useState<string | undefined>(props.message);
  const [loginErrorType, setLoginErrorType] = useState("warning");
  const [loginErrorIsSSO, setLoginErrorIsSSO] = useState(false);
  const [twoFactorInfo, setTwoFactorInfo] = useState<object | undefined>(undefined);
  const [twoFactorCode, setTwoFactorCode] = useState<string | undefined>(undefined);
  const [twoFactorCodeError, setTwoFactorCodeError] = useState<string | undefined>(undefined);

  const [passwordResetProcessing, setPasswordResetProcessing] = useState<boolean>(false);
  const [passwordResetSuccess, setPasswordResetSuccess] = useState<boolean>(false);

  const auth = useAuth();

  // Called when a key is pressed in the email field
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setEmail(e.target.value);
    if (e.target.value.length > 6 && emailError)
      setEmailError(undefined);
  }

  // Called when a key is pressed in the password field
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setPassword(e.target.value);
    if (e.target.value.length > 7 && passwordError)
      setPasswordError(undefined);
  }

  const handleTwoFactorCodeChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setTwoFactorCode(e.target.value);
    if (e.target.value.length == 6 && twoFactorCodeError) {
      setTwoFactorCodeError(undefined);
    }
  }

  // Called on submit to ensure our inputs are valid (long enough and such)
  const validateInputs = (): boolean => {
    if(twoFactorInfo !== undefined) {
      if(twoFactorCode?.length !== 6) {
        setTwoFactorCodeError(i18n.t("login.form.2fa.code-length-error"));
        return false;
      }
      return true;
    }

    let validated = true;
    if(! /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]{2,24})+$/.test(email)) {
      setEmailError(i18n.t("login.form.email-pattern-error"));
      validated = false;
    } else if (emailError) setEmailError(undefined);

    if (password.length < 8) {
      setPasswordError(i18n.t("login.form.password-length-error"));
      validated = false;
    } else if (passwordError) setPasswordError(undefined);

    return validated;
  }

  // Perform the login attempt - called by the submit button
  const doLogin = (): void => {
    if (loginError) {
      // Clear a loginError to remove the display as we try again
      setLoginError(undefined);
    }

    if (!validateInputs()) {
      // Input isn't valid; this function will set appropriate errors
      // so we can just abort this login attempt until the user corrects
      return;
    }

    // Disable the submit button while we try to log in
    setLoginInflight(true);

    // Perform the actual login attempt
    // This returns a promise, which may error
    const logInPromise = twoFactorInfo !== undefined ? auth.logInTwoFactor(twoFactorInfo.token, twoFactorCode) : auth.logIn(email, password);
    logInPromise.then(async (success: boolean) => {
      // We logged in!
      // We don't setLoginInflight here, as the callback may redirect
      // Which would then cause an error
    }).catch((reason: any) => {
      // Login failed; display the error
      setLoginInflight(false);
      if(reason?.error === "second-factor-required") {
        // two-factor authentication
        setTwoFactorInfo(reason);
      } else {
        setLoginErrorIsSSO(false);
        if (reason.code === 401 || reason.code === 403) {
          if ((reason.body?.error || "").includes(" trial ")) {
            setLoginError(reason.body.error);
          } else {
            if(twoFactorInfo !== undefined) {
              setLoginError(i18n.t("login.form.2fa.login-failure"));
            } else {
              setLoginError(i18n.t("login.form.login-failure"));
              setPasswordResetPrompt(true);
            }
          }
          setLoginErrorType("warning");
        } else {
          setLoginError(reason.message);
          setLoginErrorType("error");
        }
      }
    });
  }

  const doSSOLogin = (provider: string, token: string): void => {
    setLoginInflight(true);
    setLoginError(undefined);
    auth.logInSSO(token, provider).then(async (success: boolean) => {
      // We logged in!
      // We don't setLoginInflight here, as the callback may redirect
      // Which would then cause an error
    }).catch((reason: any) => {
      // Login failed; display the error
      setLoginInflight(false);
      if(reason?.error === "second-factor-required") {
        // two-factor authentication
        setTwoFactorInfo(reason);
      } else {
        setLoginErrorIsSSO(true);
        if (reason.code === 401 || reason.code === 403) {
          setLoginError(reason.body.error);
          setLoginErrorType("warning");
        } else {
          setLoginError(reason.message);
          setLoginErrorType("error");
        }
      }
    });
  }

  const handleSignInButtonClick = (e: React.FormEvent<EventTarget>): void => {
    e.preventDefault();
    doLogin();
  }

  const handleTwoFactorSignInClick = (e: React.FormEvent<EventTarget>): void => {
    e.preventDefault();
    doLogin();
  }

  const checkForReturn = (e: React.KeyboardEvent): void => {
    if (e.key === "Enter") {
      doLogin();
    }
  };

  function requestPasswordReset() {
    if (!email) { return; }

    setPasswordResetProcessing(true);
    APIRequest("iforgot", {email: email}, {method: "POST"}).then(() => {
      setPasswordResetProcessing(false);
      setPasswordResetSuccess(true);
    }).catch((e) => {
      setPasswordResetProcessing(false);
      enqueueSnackbar(`${e}: ${e}`, {variant: 'error', persist: true});
    });
  }


  if(twoFactorInfo !== undefined) {
    return (
      <form onSubmit={handleTwoFactorSignInClick}>
        <Typography variant="h6" sx={{
          mb: 2
        }}>
          {i18n.t("login.form.2fa."+twoFactorInfo.type)}
        </Typography>

        <div>
          <TextField fullWidth autoFocus
            key="login-form-2fa-code"
            autoComplete="off"
            label={i18n.t("login.form.2fa.code")}
            variant="outlined"
            placeholder="ABC123"
            disabled={loginInflight}
            error={twoFactorCodeError !== undefined}
            helperText={twoFactorCodeError}
            onChange={handleTwoFactorCodeChange}
            onKeyPress={checkForReturn}
            slotProps={{
              input: {
                startAdornment:
                  <InputAdornment position="start">
                    <Key />
                  </InputAdornment>
              }
            }}
          />

          <div style={{ marginTop: "1rem" }} />

          <Button fullWidth
            onClick={handleTwoFactorSignInClick}
            loading={loginInflight}
            variant="outlined"
            color="success" >
            {i18n.t("login.form.signin-button")}
          </Button>

          <div style={{ marginTop: "1rem" }} />

          {loginError &&
            <Grow in={loginError !== undefined}>
              <Alert severity={loginErrorType as AlertColor}>
                {loginError}
              </Alert>
            </Grow>
          }

        </div>
      </form>
    );
  }

  return (
    <form onSubmit={handleSignInButtonClick}>
      <Typography variant="h6" sx={{
        mb: 2
      }}>
        {i18n.t("login.form.header")}
      </Typography>

      <div>
        <TextField fullWidth autoFocus
          key="login-form-email"
          label={i18n.t("login.form.email")}
          autoComplete="email"
          variant="outlined"
          type={"email"}
          placeholder="you@example.com"
          disabled={loginInflight}
          error={emailError !== undefined}
          helperText={emailError}
          onChange={handleEmailChange}
          onKeyPress={checkForReturn}
          slotProps={{
            input: {
              startAdornment:
                <InputAdornment position="start">
                  <AccountCircle />
                </InputAdornment>
            }
          }}
        />

        <div style={{ marginTop: "1rem" }} />

        <TextField fullWidth
          key="login-form-password"
          label={i18n.t("login.form.password")}
          autoComplete="current-password"
          variant="outlined"
          type="password"
          disabled={loginInflight}
          error={passwordError !== undefined}
          helperText={passwordError}
          onChange={handlePasswordChange}
          onKeyPress={checkForReturn}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Lock />
                </InputAdornment>
              ),
            }
          }} />

        <div style={{ marginTop: "1rem" }} />

        <Button fullWidth
          onClick={handleSignInButtonClick}
          loading={loginInflight}
          variant="outlined"
          color="success" >
          {i18n.t("login.form.signin-button")}
        </Button>

        <div style={{ marginTop: "1rem" }} />

        {!loginErrorIsSSO && loginError && !passwordResetSuccess &&
          <Grow in={loginError !== undefined}>
            <Alert severity={loginErrorType as AlertColor} style={{ marginBottom: "1rem" }}>
              {loginError}
              {passwordResetPrompt &&
                <Box sx={{ textAlign: "center", mr:2 }}>
                  <Button
                    onClick={requestPasswordReset}
                    variant="text"
                    disabled={passwordResetProcessing}
                    color="primary">
                    {i18n.t("login.form.forgot-password")}
                  </Button>
                  {passwordResetProcessing &&
                    <TextLoader />
                  }
                </Box>
              }
            </Alert>
          </Grow>
        }

        {passwordResetSuccess &&
          <Grow in={passwordResetSuccess}>
            <Alert severity="success">
              {i18n.t('login.password.request-reset-in-flight')}
            </Alert>
          </Grow>
        }

        <Typography
          variant="h6"
          sx={{
            mt: "2rem",
            mb: "1rem",
            textAlign: "center"
          }}>
          {i18n.t("login.form.or-sso")}
        </Typography>

        <Stack sx={{gap: 1, alignItems: "center"}}>
          <GoogleLogin
            // fixed width of 215px to match the fixed width of the MicrosoftLogin button
            width="215px"
            onSuccess={credentialResponse => {
              doSSOLogin("google", credentialResponse.credential);
            }}
            onError={() => {
              setLoginError(i18n.t("login.form.google-sso-failed"));
              setLoginErrorIsSSO(true);
            }}
          />
          <MicrosoftLogin
            clientId="26ac65cb-ea96-4ed1-9b3a-cc6096dc95a5"
            redirectUri={window.location}
            authCallback={(err, authData) => {
              if(err) {
                console.error(`Microsoft SSO err: ${err}`);
                Sentry.captureMessage(`Microsoft SSO err: ${err}`);
                if(err.toString().includes("popup")) {
                  setLoginError(i18n.t("login.form.popup-blocked"));
                } else {
                  setLoginError(i18n.t("login.form.microsoft-sso-failed"));
                }
                setLoginErrorIsSSO(true);
              } else {
                doSSOLogin("microsoft", authData.idToken);
              }
            }}
          />
        </Stack>
      </div>
    </form>
  );
}
